import Device, {
  DeviceBase,
  WARNING_STATES,
} from '@data/device/model/device'
import { HOT_CO2 } from './constants'
import { FaBan } from 'react-icons/fa'
import moment from 'moment-timezone'
import {
  conversionFactors,
  PULSES_VALUES,
} from '@components/main/DeviceManagement/DeviceInformationManager/constants'
import { HourlyUsage } from '@data/waterUsage/model/waterUsage.model'
import React from 'react'
import { differenceInDays } from 'date-fns'

const CUBIC_METER_VOLUME = 1000
const CUBIC_FOOT_VOLUME = 7.48


export function roundToTwo(num: number) {
  return Math.round((num + Number.EPSILON) * 100) / 100
}

export function convertToUnitOfVolume(uom: string, value: number) {
  const defaultValue = `${roundToTwo(value / CUBIC_METER_VOLUME)} m${String.fromCharCode(179)}`

  switch (uom) {
    case 'Gallons(US)':
      // return `${roundToTwo(value / CUBIC_FOOT_VOLUME)} f${String.fromCharCode(179)}`
      return `${roundToTwo(value / CUBIC_METER_VOLUME)} (1000 Gallons)`

    case 'Liters':
      return defaultValue
    default:
      return defaultValue
  }
}

export const calculateCo2Produced = (
  uom: string,
  totalUsage: number,
  selectedDevices: Device[],
  type: 'hot' | 'cold' = 'cold',
) => {
  const devicesCo2Rate = type === 'hot' ? HOT_CO2 : calculateCo2RateAverage(selectedDevices)

  switch (uom) {
    case 'Gallons(US)':
      return {
        value: roundToTwo((totalUsage / CUBIC_FOOT_VOLUME) * devicesCo2Rate),
        unit: 'Kg',
      }

    case 'Liters':
      return {
        value: roundToTwo((totalUsage / CUBIC_METER_VOLUME) * devicesCo2Rate),
        unit: 'Kg',
      }
    default:
      return {
        value: roundToTwo((totalUsage / CUBIC_FOOT_VOLUME) * devicesCo2Rate),
        unit: 'Kg',
      }
  }
}

export const itemFromPath = (path: string, dataObject: any) => {
  // Split the path into its components
  const pathComponents = path.split('.')

  // Initialize a variable to hold the current value as we traverse the path
  let currentValue = dataObject

  // Iterate over each component in the path
  for (const component of pathComponents) {
    // If the current value is null or undefined, return null
    if (currentValue == null) {
      return '-'
    }

    // Update the current value to the next level in the object
    currentValue = currentValue[component]
  }

  // Handle boolean values
  if (currentValue === true) {
    return 'Yes'
  } else if (currentValue === false) {
    return 'No'
  }

  // Handle empty string or null
  if (currentValue === '' || currentValue == null) {
    return '-'
  }

  // Return the current value for other cases
  return currentValue
}

export const sortObjectsByKey = (objArray: any, key: string, order: string = 'asc') => {
  if (key.toLocaleLowerCase().includes('date')) {
    return objArray.sort((a: any, b: any) => {
      let dateA: any | moment.Moment
      let dateB: any | moment.Moment

      if (key.toLocaleLowerCase().includes('location')) {
        dateA = moment(a.settings.onboardingDate)
        dateB = moment(b.settings.onboardingDate)
      } else {
        dateA = moment(a.deviceSettings.installDate)
        dateB = moment(b.deviceSettings.installDate)
      }

      if (order === 'ASC') {
        return dateA.diff(dateB)
      } else {
        return dateB.diff(dateA)
      }
    })
  } else if (key.toLocaleLowerCase().includes('name')) {
    return objArray.sort((a: any, b: any) => {
      const nameA = a[key].toLowerCase()
      const nameB = b[key].toLowerCase()

      if (order === 'ASC') {
        return nameA.localeCompare(nameB)
      } else {
        return nameB.localeCompare(nameA)
      }
    })
  } else {
    return objArray.sort((a: any, b: any) => {
      const firstItem = itemFromPath(key, a)
      const lastItem = itemFromPath(key, b)
      const x = firstItem?.length ? firstItem.length : firstItem
      const y = lastItem?.length ? lastItem.length : lastItem
      if (order.toLowerCase() === 'asc') {
        if (x < y) {
          return -1
        }
        if (x > y) {
          return 1
        }
      } else if (order.toLowerCase() === 'desc') {
        if (x > y) {
          return -1
        }
        if (x < y) {
          return 1
        }
      }

      return 0
    })
  }
}

export const getRandomDarkColor = () => {
  let letters = '012345'.split('')
  let color = '#'
  color += letters[Math.round(Math.random() * 5)]
  letters = '0123456789ABCDEF'.split('')
  for (let i = 0; i < 5; i++) {
    color += letters[Math.round(Math.random() * 15)]
  }
  return color
}

export const hashCode = (str: string) => {
  // java String#hashCode
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return hash
}

export const intToRGB = (i: number) => {
  const c = (i & 0x00ffffff).toString(16).toUpperCase()

  return '00000'.substring(0, 6 - c.length) + c
}

export const hexToRgb = (hex: string) => {
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null
}

export const componentToHex = (c: number) => {
  let hex = c.toString(16)
  return hex.length === 1 ? '0' + hex : hex
}

export const rgbToHex = (r: number, g: number, b: number) => {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b)
}

// Examples:
// - P01_0005Z_95(REMOTE) Sep  3 2020 15:22:21
// - P01_0005Z_79(210)
// - P01_0005Z_87(2000)_TEST_RTCC Jul 14 2020 09:11:55
// - SM_V3_016 Feb 11 2021 14:50:43 Feb 11 2021 7 2 1
export const formatFirmwareName = (firmware: string) => {
  let formattedFirmware = ''
  if (firmware) {
    formattedFirmware = firmware
    let fw_prefix: string
    // If it's V2
    if (firmware.includes('P01')) {
      fw_prefix = 'P01_0005Z_'
    } else {
      fw_prefix = 'SM_V3_'
    }
    const substr = firmware.split(fw_prefix)[1]
    const match = substr.match(/\d+/)
    if (match) {
      const versionNumber = match[0]
      formattedFirmware = `${fw_prefix}${versionNumber}`
    }
  }
  return formattedFirmware
}

export const capitalizeFirstLetter = (s: string) => (s && s[0].toUpperCase() + s.slice(1)) || null

export const formatPercentage = (value: number) =>
  Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

export const validatePhoneNumberStructure = (phone: string) => {
  const phoneNumberRegex = /^\+?\d{1,3}?(?:[-\s()]?\d{3}){2,3}$/
  return phoneNumberRegex.test(phone)
}

// export const isArrayContained = (arr: string[], target: string[]) =>
//   target.every((v) => arr.includes(v))


export const formatCurrency = (value: number, currency: string) =>
  value.toLocaleString('en-GB', {
    style: 'currency',
    currency,
  })

export const filterDevicesDueTo30DaysReport = (devices: Device[]) => {
  return devices.filter(
    (device) =>
      !device.deviceSettings.report30Day &&
      moment() >= moment(device.deviceSettings.monitoringStartDate).add(30, 'days'),
  )
}

export const filterDevicesWithValveErrors = (devices: Device[]) => {
  return devices.filter((device) =>
    Object.values(device.valves || {}).some((valve) => WARNING_STATES.includes(valve.state)),
  )
}

// State 7 is NOT_FITTED — same "installed" definition used by the Valves tab's stat card.
export const countInstalledValves = (device: Device): number =>
  Object.values(device.valves || {}).filter((valve) => valve.state !== 7).length

export const filterNewlyInstalledDevices = (devices: Device[]) => {
  const today = moment()
  const lastWeek = today.clone().subtract(7, 'days')

  return devices.filter((device) => {
    const installDate = moment(device.deviceSettings.installDate)
    return installDate.isBetween(lastWeek, today, 'day', '[]')
  })
}


// The complete set of values getPowerStatus can return, kept beside it and used as its return
// type. A dropdown or filter that offers these therefore cannot drift from what the function
// actually produces: adding a branch that returns something new is a type error until the value
// is added here too. An empty string is deliberately not a member — it means no status could be
// derived, which callers render as a ban icon rather than as text.
export const POWER_STATUS_VALUES = [
  'Plugged',
  'Battery Power',
  'Internal Battery',
  'External Battery',
] as const

export type PowerStatus = (typeof POWER_STATUS_VALUES)[number]

export const getPowerStatus = (device: DeviceBase): PowerStatus | '' => {
  let status: PowerStatus | '' = ''
  if (device.deviceVendor == 'METASPHERE') {
    if (device.batteryLevel && device.batteryLevelExt) {
      // MetaSphere devices
      if (device.batteryLevelExt > 5500) {
        status = 'External Battery'
      } else {
        status = 'Internal Battery'
      }
    } else {
      status = 'Internal Battery'
    }
  } else if (device.batteryStatus) {
    // Possible status: BATTERY_LOW, BATTERY_OK, BATTERY_FULL, DC_POWER_ON for SF Hubs
    if (device.batteryStatus.toUpperCase() === 'DC_POWER_ON') {
      status = 'Plugged'
    } else {
      status = 'Battery Power'
    }
  }
  return status
}

export const getDeviceStatus = (
  device: Device,
  hasDashboardStatusViewPermission: boolean,
) => {
  if (device.deviceSettings?.uploadFreqMins) {
    if (!device.lastOnline) {
      return <FaBan />
    } else {
      const interval = hasDashboardStatusViewPermission
        ? device.deviceSettings?.uploadFreqMins * 2
        : device.deviceSettings?.uploadFreqMins * 12
      if (moment.utc().diff(device.lastOnline, 'minutes') >= interval) {
        return 'Offline'
      } else {
        return 'Online'
      }
    }
  }
}

export const compareObjects = (obj1: any, obj2: any) => {
  const changes = []

  for (const key in obj1) {
    if (obj1.hasOwnProperty(key)) {
      if (obj1[key] !== obj2[key]) {
        changes.push(`${key} was updated from "${obj1[key]}" to "${obj2[key]}"`)
      }
    }
  }
  return changes
}

// export const PULSES_OPTIONS: { value: string; label: string }[] = PULSES_VALUES.map((pulse) => {
//   return {
//     value: pulse,
//     label:
//       parseInt(pulse) > 1
//         ? `${pulse} - ${pulse} Pulses/liter`
//         : `${pulse} - ${1 / parseFloat(pulse)} Liters/pulse`,
//   }
// })

export const pulseOptions = (uom: string) => {
  return PULSES_VALUES.map((pulse) => {
    return {
      value: pulse,
      label:
        parseInt(pulse) > 1
          ? `${pulse} Pulses/${uom}`
          : `${1 / parseFloat(pulse)} ${uom}/pulse`
    }
  })
}

export const extractFlow = (data: any[], key: string): HourlyUsage[] => {
  return data.map((usage) => {
    return {
      date: usage.date,
      utc_date: usage.utcDate,
      value: key === 'upperFlowLimit' ? usage[key] : usage[key] / 1,
      rawValue: usage[key],
    }
  })
}

export const calculateCo2RateAverage = (devices: Device[]) => {
  const sumCo2Rates = devices.reduce((acc, device) => {
    if (device.deviceSettings && device.deviceSettings.co2Rate) {
      return acc + device.deviceSettings.co2Rate
    }
    return acc
  }, 0)

  const averageCo2Rate = sumCo2Rates / devices.length

  return roundToTwo(averageCo2Rate)
}

export const stringToBool = (e: string | boolean | undefined): boolean => {
  if (e === 'true' || e === 'false') {
    return JSON.parse(e)
  } else {
    return false
  }
}

export const handleStringToBool = (e: string, setCallback: React.Dispatch<any>) => {
  let updatedValue: boolean
  if (e === 'true' || e === 'false') {
    updatedValue = JSON.parse(e)
    setCallback(updatedValue)
  }
}

export const boolToStringDefault = (val: boolean) => {
  return val ? { label: 'Yes', value: 'true' } : { label: 'No', value: 'false' }
}

export const convertCost = (deviceConversionFactor: number, UOM: keyof typeof conversionFactors, cost: number) => {
  return (conversionFactors[UOM] / deviceConversionFactor) * cost
}



export const calculateBatteryPercentage = (batteryLevel: number, deviceType: string) => {
  let battPct = batteryLevel
  switch (deviceType) {
    case '3V004': {
      const maxV = 7000
      const vBatt = batteryLevel > maxV ? 7000 : batteryLevel
      const maxVDelta = 2000
      const vDelta = maxVDelta - (maxV - vBatt)
      battPct = Math.round((vDelta < 0 ? 0 : vDelta / maxVDelta) * 100)
      return battPct
    }
    case 'POINT GREEN 3G': {
      const maxV = 7010
      const vBatt = batteryLevel > maxV ? 7010 : batteryLevel
      const maxVDelta = 1500
      const vDelta = maxVDelta - (maxV - vBatt)
      battPct = Math.round((vDelta / maxVDelta) * 100)
      return battPct
    }
    default:
      return battPct
  }
}
// const isDateToday = (today: Date, eventDate: Date) => {
//   return eventDate.getUTCDate() === today.getDate() &&
//     eventDate.getUTCMonth() === today.getMonth() &&
//     eventDate.getUTCFullYear() === today.getFullYear()
// }

// Helper function to calculate duration in days
export function calculateDurationInDays(issueStartDate: string): number {
  const startDate = new Date(issueStartDate)
  const now = new Date()
  return differenceInDays(now, startDate)
}